/**
 * PipelineStepSettings — slide-over panel for editing a single pipeline
 * step or pipeline-level settings.
 *
 * Store-connected: reads from useEditorStore, usePreferencesStore, usePresetStore.
 * Pipeline-level settings are delegated to PipelineSettingsPanel.
 */

import { Trash2 } from 'lucide-react';
import React from 'react';

import { AttributeInput } from '@/components/common/AttributeInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToolCatalog } from '@/hooks/useToolCatalog';
import type { IToolPreset } from '@/interfaces/IStorage';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { usePreferencesStore } from '@/store/usePreferencesStore';
import { usePresetStore } from '@/store/usePresetStore';

import { PipelineSettingsPanel } from './PipelineSettingsPanel';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PipelineStepSettingsProps {
  stepId: string | null;
  pipelineSettingsOpen: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PipelineStepSettings: React.FC<PipelineStepSettingsProps> = ({
  stepId,
  pipelineSettingsOpen,
}) => {
  const catalog = useToolCatalog();
  const { updateToolConfig } = useAppStore();
  const pipeline = useEditorStore((s) => s.pipeline);
  const setStepLabel = useEditorStore((s) => s.setStepLabel);
  const showToolNameWithStepLabel = usePreferencesStore((s) => s.showToolNameWithStepLabel);
  const activePipelineName = useEditorStore((s) => s.activePipelineName);
  const activePipelineDescription = useEditorStore((s) => s.activePipelineDescription);

  const presets = usePresetStore((s) => s.presets);
  const createPreset = usePresetStore((s) => s.createPreset);
  const updatePreset = usePresetStore((s) => s.updatePreset);
  const deletePreset = usePresetStore((s) => s.deletePreset);

  const step = stepId ? pipeline.find((p) => p.id === stepId) : undefined;
  const tool = step ? catalog.getToolById(step.toolId) ?? null : null;

  const presetsForTool = React.useMemo<IToolPreset[]>(() => {
    if (!tool) return [];
    return presets.filter((p) => p.toolId === tool.id).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [presets, tool]);

  const [selectedStepPresetId, setSelectedStepPresetId] = React.useState<string>('');
  const selectedStepPreset = React.useMemo(
    () => (selectedStepPresetId ? presetsForTool.find((p) => p.id === selectedStepPresetId) ?? null : null),
    [presetsForTool, selectedStepPresetId],
  );

  const [stepPresetName, setStepPresetName] = React.useState('');

  React.useEffect(() => { setSelectedStepPresetId(''); setStepPresetName(''); }, [stepId]);
  React.useEffect(() => { if (selectedStepPreset) setStepPresetName(selectedStepPreset.name); }, [selectedStepPreset?.id]);

  if (!pipelineSettingsOpen && (!step || !tool)) return null;

  const stepLabel = step?.label?.trim();
  const stepTitle = stepLabel
    ? showToolNameWithStepLabel ? `${stepLabel} · ${tool?.name ?? ''}` : stepLabel
    : tool?.name;

  const title = pipelineSettingsOpen ? 'Pipeline Settings' : stepTitle;
  const subtitle = pipelineSettingsOpen ? (activePipelineName || 'Pipeline') : (tool?.category ?? 'Tool');
  const description = pipelineSettingsOpen ? activePipelineDescription : tool?.description;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Info header */}
      <div className="border-b border-border px-6 py-3 shrink-0">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
          <div className="text-base font-semibold truncate">{title}</div>
          {description && <div className="text-xs text-muted-foreground mt-1 truncate">{description}</div>}
        </div>
      </div>

      <div className="p-6 overflow-auto flex-1 min-h-0">
        {pipelineSettingsOpen ? (
          <PipelineSettingsPanel />
        ) : (
          <div className="space-y-6">
            {/* Step name */}
            <div className="space-y-1">
              <div className="text-lg font-semibold">Step name</div>
              <Input value={step!.label ?? ''} onChange={(e) => setStepLabel(step!.id, e.target.value)}
                placeholder={tool?.name ?? 'Step name'} />
            </div>

            {/* Presets */}
            <div className="space-y-2">
              <div className="text-lg font-semibold">Presets</div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-64">
                  <Select value={selectedStepPresetId || "__none__"}
                    onValueChange={(value) => {
                      const val = value === "__none__" ? "" : value;
                      setSelectedStepPresetId(val);
                      setStepPresetName(presetsForTool.find((p) => p.id === val)?.name ?? '');
                    }}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select preset..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {presetsForTool.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-64">
                  <Input value={stepPresetName} onChange={(e) => setStepPresetName(e.target.value)}
                    placeholder={selectedStepPreset ? 'Preset name' : 'New preset name'} className="h-8" />
                </div>

                {selectedStepPreset && (
                  <Button variant="secondary" size="sm"
                    onClick={() => updateToolConfig(step!.id, { ...(selectedStepPreset.config ?? {}) })}
                    title="Apply this preset to the step">
                    Apply
                  </Button>
                )}

                <Button variant="secondary" size="sm" onClick={() => {
                  if (!tool) return;
                  const created = createPreset({ toolId: tool.id, name: stepPresetName, config: step!.config });
                  setSelectedStepPresetId(created.id);
                  setStepPresetName(created.name);
                }} title="Create a preset from this step's current settings">
                  Create
                </Button>

                {selectedStepPreset && (
                  <>
                    <Button variant="secondary" size="sm"
                      onClick={() => updatePreset(selectedStepPreset.id, { name: stepPresetName, config: step!.config })}
                      title="Update this preset with this step's current settings">
                      Update
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 px-0"
                            onClick={() => { deletePreset(selectedStepPreset.id); setSelectedStepPresetId(''); setStepPresetName(''); }}
                            aria-label="Delete preset">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Delete preset</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </div>

            {/* Configuration */}
            <div>
              <div className="text-lg font-semibold">Configuration</div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {(tool?.attributes ?? []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">This step has no settings.</div>
                ) : (
                  (tool?.attributes ?? []).map((attr) => (
                    <div key={attr.name} className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">{attr.label}</label>
                      <AttributeInput attr={attr} value={step!.config[attr.name]} compact
                        onChange={(name, value) => updateToolConfig(step!.id, { ...step!.config, [name]: value })} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


